"use strict"

import { scaleLinear } from "d3-scale"
import React from "react"

import { isNumber } from "../../../utils"

/*
 * This component is shared and *identical* between LanzaLake and LTPM
 * All changes should be generalized and added to any registration as needed
 *  */
export default class ZoomThumbnail extends React.Component {
  static defaultProps = {
    width: 100,
    height: 80,
    selectionBackground: "#aabbee",
    selectionOutline: "var(--lanzalake-blue-primary)",
  }

  constructor(props) {
    super(props)

    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)
  }

  componentWillUnmount() {
    this.detatchListeners()
  }

  axesFromProps() {
    const { width, height, zoomRanges = {}, axesAndRanges = {}, timeExtractor, graphKey } = this.props

    const timeRange = axesAndRanges["time"] || [],
      minFullTime = timeRange[0],
      maxFullTime = timeRange[1]
    const timeZoom = zoomRanges.time || [],
      minTime = timeZoom[0] !== undefined ? timeExtractor.offsetDays(minFullTime, timeZoom[0]) : minFullTime,
      maxTime = timeZoom[1] !== undefined ? timeExtractor.offsetDays(minFullTime, timeZoom[1]) : maxFullTime

    function makeAxisRep(axis, zoom, range) {
      if (axis) {
        return {
          scale: scaleLinear().domain(axis).range(range),
          current: zoom || isNumber(zoom) ? zoom : axis,
        }
      }
    }

    const axis1domain = graphKey && graphKey.axis1domain ? graphKey.axis1domain : null
    const axis2domain = graphKey && graphKey.axis2domain ? graphKey.axis2domain : null
    function getRange(range, domain_range) {
      let axis_range = []
      if (domain_range && range) {
        axis_range = [
          isNumber(domain_range[0]) ? domain_range[0] : range[0],
          isNumber(domain_range[1]) ? domain_range[1] : range[1],
        ]
      } else {
        axis_range = range
      }
      return axis_range
    }

    let y1Axis = getRange(axesAndRanges["y"], axis1domain)
    let y2Axis = getRange(axesAndRanges["y2"], axis2domain)

    const axes = {
      y: makeAxisRep(y1Axis, zoomRanges.y, [height, 0]),
      y2: makeAxisRep(y2Axis, zoomRanges.y2, [height, 0]),
    }

    if (axesAndRanges["time"]) {
      axes["x"] = {
        // Math.min and Math.max here as the user could manually set a range outside the range in axesAndRanges["time"]
        scale: timeExtractor.buildAxis(
          Math.min(minFullTime, minTime),
          Math.max(maxFullTime, maxTime),
          0,
          width,
        ),
        current: [minTime, maxTime],
      }
      axes.xIsTime = true
    } else if (axesAndRanges["x"]) {
      axes["x"] = makeAxisRep(axesAndRanges["x"], zoomRanges.x, [0, width])
    }
    return axes
  }

  render() {
    const { width, height } = this.props
    let { x, y } = this.axesFromProps(this.props)

    if (!x || typeof x.current[0] === "undefined" || typeof x.current[1] === "undefined") {
      return <svg width={width} height={height} />
    }

    if (!y) {
      // Make a dummy y axis for cases -- like the global zoom control on SingleRunView -- where it isn't relevant.
      y = {
        current: [0, 1],
        scale: scaleLinear().domain([0, 1]).range([height, 0]),
      }
    }

    return (
      <svg width={width} height={height}>
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          stroke="var(--lanzalake-black)"
          fill="white"
          ref={(clickCatcher) => {
            this.clickCatcher = clickCatcher
          }}
          onMouseDown={this.onMouseDown}
        />

        <rect
          x={x.scale(x.current[0])}
          y={y.scale(y.current[1])}
          width={x.scale(x.current[1]) - x.scale(x.current[0])}
          height={y.scale(y.current[0]) - y.scale(y.current[1]) || height}
          fill={this.props.selectionBackground}
          stroke={this.props.selectionOutline}
          style={{ pointerEvents: "none" }}
        />
      </svg>
    )
  }

  /**
   * Helper for projecting pointer event positions relative to the zoom widget.
   * Note that (unlike previous versions), there's no position clamping.  All
   * clamping happens in handleDrag.
   */

  target(ev) {
    const catcherBox = this.clickCatcher.getBoundingClientRect()
    return [ev.clientX - catcherBox.x, ev.clientY - catcherBox.y]
  }

  detatchListeners() {
    const rootNode = document.getElementById("application-page")
    rootNode.removeEventListener("mousemove", this.onMouseMove, false)
    rootNode.removeEventListener("mouseup", this.onMouseUp, false)
  }

  onMouseDown(ev) {
    const [tx, ty] = this.target(ev)
    const axes = this.axesFromProps(this.props)

    this.dragTargetX = tx
    this.dragTargetY = ty
    this.dragTimeExtractor = this.props.timeExtractor
    this.dragOriginAxes = axes

    const rootNode = document.getElementById("application-page")
    rootNode.addEventListener("mousemove", this.onMouseMove, false)
    rootNode.addEventListener("mouseup", this.onMouseUp, false)
  }

  onMouseMove(ev) {
    const [tx, ty] = this.target(ev)
    this.handleDrag(tx, ty)
  }

  onMouseUp(ev) {
    const [tx, ty] = this.target(ev)
    this.handleDrag(tx, ty)

    this.detatchListeners()
  }

  handleDrag(tx, ty) {
    const timeExtractor = this.dragTimeExtractor

    if (this.props.zoomConfig) {
      const yUpdate = (axis) => {
        if (!axis) return

        const { scale, current } = axis,
          span = current[1] - current[0]

        const [minFull, maxFull] = scale.domain()
        let newMin = Math.max(minFull, scale.invert(scale(current[0]) + ty - this.dragTargetY))
        let newMax = Math.min(newMin + span, maxFull)
        newMin = newMax - span

        return [newMin, newMax]
      }

      const xUpdate = (axis) => {
        if (!axis) return

        const { scale, current } = axis,
          span = current[1] - current[0]

        const [minFull, maxFull] = scale.domain()
        let newMin = Math.max(minFull, scale.invert(scale(current[0]) + tx - this.dragTargetX))
        let newMax = Math.min(newMin + span, maxFull)
        newMin = newMax - span

        return [newMin, newMax]
      }

      // Although we make two separate calls here, React's update batching should ensure that
      // these never get separated.

      if (this.dragOriginAxes.xIsTime) {
        const { scale, current } = this.dragOriginAxes.x,
          span = timeExtractor.difference(current[1], current[0])
        const [minFull, maxFull] = scale.domain()

        let newMinTime = timeExtractor.max(minFull, scale.invert(scale(current[0]) + tx - this.dragTargetX))
        let newMaxTime = timeExtractor.min(timeExtractor.offsetDays(newMinTime, span), maxFull)
        newMinTime = timeExtractor.offsetDays(newMaxTime, -span)

        this.props.zoomConfig.zoomTime(
          timeExtractor.difference(newMinTime, this.props.baseTime),
          timeExtractor.difference(newMaxTime, this.props.baseTime),
        )
      }

      if (this.props.graphKey) {
        this.props.zoomConfig.zoomPlot(this.props.graphKey, this.props.zoomKey, {
          y: yUpdate(this.dragOriginAxes.y),
          y2: yUpdate(this.dragOriginAxes.y2),
          x: this.dragOriginAxes.xIsTime ? undefined : xUpdate(this.dragOriginAxes.x),
        })
      }
    }
  }
}
